import { Menu } from 'primereact/menu';
import { useRef, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useAtom } from 'jotai';
import { globalSearchRefAtom } from '~/atoms/globalSearch';
import { useRBAC } from '~/hooks/useRBAC';

interface HeaderMenuProps {
  onResetLayout?: () => void;
  onEditPortfolio?: () => void;
  onSendFeedback?: () => void;
  onAddWidget?: () => void;
  onCreatePortfolio?: () => void;
  isDashboard?: boolean;
  portfolioId?: string;
}

export const HeaderMenu: React.FC<HeaderMenuProps> = ({
  onResetLayout,
  onEditPortfolio,
  onSendFeedback,
  onAddWidget,
  onCreatePortfolio,
  isDashboard = false,
  portfolioId,
}) => {
  const menuRef = useRef<Menu>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [globalSearch] = useAtom(globalSearchRefAtom);
  const { canCreate } = useRBAC();

  // Force z-index with CSS injection
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .p-menu-overlay {
        z-index: 9999999 !important;
      }
      .p-menu {
        z-index: 9999999 !important;
      }
      [data-pc-section="root"] {
        z-index: 9999999 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const auth = useAuth();

  const menuItems = [
    // Send Feedback - always available
    {
      label: 'Send Feedback',
      icon: 'pi pi-send',
      command: onSendFeedback,
      className: 'text-blue-500',
    },

    // Add Widget - only for dashboards
    ...(isDashboard
      ? [
          {
            label: 'Add Widget',
            icon: 'pi pi-plus-circle',
            command: () => {
              if (globalSearch) {
                globalSearch.show('Widgets');
              }
            },
            className: 'text-blue-500',
          },
        ]
      : []),

    // Reset Layout - only for dashboards
    ...(isDashboard && onResetLayout
      ? [
          {
            label: 'Reset Layout',
            icon: 'pi pi-undo',
            command: onResetLayout,
            className: 'text-blue-500',
          },
        ]
      : []),

    // Create New Portfolio - only if user has permission
    ...(onCreatePortfolio && canCreate('portfolio')
      ? [
          {
            label: 'Create New Portfolio',
            icon: 'pi pi-plus',
            command: onCreatePortfolio,
            className: 'text-green-500',
          },
        ]
      : []),

    // Edit Portfolio - only for organizations that require portfolios
    ...(portfolioId
      ? [
          {
            label: 'Edit Portfolio',
            icon: 'pi pi-pencil',
            command: onEditPortfolio,
            className: 'text-blue-500',
          },
        ]
      : []),
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <button
        style={{
          color: 'var(--bq-text-primary)',
          fontSize: '1.2rem',
          padding: '0.5rem',
          backgroundColor: 'transparent',
          borderRadius: '4px',
          cursor: 'pointer',
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          transition: 'transform 0.3s ease, color 0.3s ease',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setIsMenuOpen(!isMenuOpen);
          menuRef.current?.toggle(e);
        }}
      >
        <span
          style={{
            transform: isMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            display: 'inline-block',
            fontSize: isMenuOpen ? '1rem' : '1.2rem',
            lineHeight: '1',
          }}
        >
          {isMenuOpen ? '✕' : '☰'}
        </span>
      </button>
      <Menu
        ref={menuRef}
        model={menuItems}
        popup
        style={{ zIndex: 999999 }}
        onShow={() => setIsMenuOpen(true)}
        onHide={() => setIsMenuOpen(false)}
        pt={{
          root: {
            style: {
              zIndex: 999999,
            },
          },
        }}
      />
    </div>
  );
};
